import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';
import { useYouTubeAccountReset } from '@/lib/youtubeConnection';
import { formatApiError } from '@/lib/formatApiError';
import { formatCount } from '@/lib/channel';
import { ChannelSectionNav } from '@/components/channel/ChannelSectionNav';

export function ChannelKeywordsPage() {
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searching, setSearching] = useState(false);
  const [peerPhrases, setPeerPhrases] = useState([]);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/channel/keywords');
      setItems(Array.isArray(data?.items) ? data.items : []);
    } catch (err) {
      toast.error(formatApiError(err, 'Could not load keyword ideas'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    api.get('/channel/peers')
      .then(({ data }) => setPeerPhrases(Array.isArray(data?.phrases) ? data.phrases : []))
      .catch(() => setPeerPhrases([]));
  }, []);

  const clearChannel = useCallback(() => {
    setItems([]);
    setPeerPhrases([]);
  }, []);
  useYouTubeAccountReset(clearChannel);

  const searchPhrase = async (phrase) => {
    const cleaned = String(phrase || '').trim();
    if (cleaned.length < 2) return;
    setSearching(true);
    try {
      const { data } = await api.post('/channel/keywords', { query: cleaned });
      setItems((prev) => [data, ...prev.filter((row) => row.query !== data.query)]);
      setQuery('');
    } catch (err) {
      toast.error(formatApiError(err, 'Keyword search failed'));
    } finally {
      setSearching(false);
    }
  };

  const search = async (event) => {
    event.preventDefault();
    await searchPhrase(query);
  };

  return (
    <main className="p-4 sm:p-8 space-y-4 font-jakarta overflow-x-hidden">
      <div className="space-y-2">
        <h1 className="text-lg font-bold text-white">Keywords</h1>
        <p className="text-xs text-[#7d8799] max-w-2xl">
          Ideas come from YouTube search results and whether this channel already posted the phrase. This is not a paid search-volume database.
        </p>
        <ChannelSectionNav />
      </div>
      {items.some((item) => item.source === 'competitor_outlier') ? (
        <section className="space-y-2" data-testid="outlier-keyword-chips">
          <h2 className="text-sm font-bold text-white">From outlier videos</h2>
          <div className="flex flex-wrap gap-2">
            {items.filter((item) => item.source === 'competitor_outlier').map((item) => (
              <span key={item.query} className="rounded-full border border-[#214f48] bg-[#13322d] px-3 py-1.5 text-xs text-white">
                {item.query}
              </span>
            ))}
          </div>
        </section>
      ) : null}
      {peerPhrases.length > 0 ? (
        <section className="space-y-2">
          <h2 className="text-sm font-bold text-white">From channels like yours</h2>
          <div className="flex flex-wrap gap-2">
            {peerPhrases.map((phrase) => (
              <button
                key={phrase}
                type="button"
                disabled={searching}
                onClick={() => searchPhrase(phrase)}
                className="rounded-full border border-[#1f2635] px-3 py-1.5 text-xs text-white hover:border-[#34d399]"
              >
                {phrase}
              </button>
            ))}
          </div>
        </section>
      ) : null}
      <form onSubmit={search} className="flex gap-2">
        <input
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          placeholder="Phrase to research"
          className="h-10 flex-1 max-w-md px-3 rounded-full bg-[#12151d] border border-[#1f2635] text-sm text-white"
        />
        <button type="submit" disabled={searching} className="h-10 px-4 rounded-full bg-[#34d399] text-[#06211b] text-xs font-bold inline-flex items-center gap-1.5">
          {searching && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
          Search
        </button>
      </form>
      {loading ? <p className="text-xs text-[#7d8799]">Loading saved searches…</p> : null}
      <div className="grid md:grid-cols-2 gap-3">
        {items.map((item) => (
          <article key={`${item.query}-${item.created_at || ''}`} className="rounded-[16px] border border-[#1f2635] bg-[#12151d] p-4 space-y-2">
            <div className="flex items-start justify-between gap-3">
              <h2 className="text-sm font-bold text-white">{item.query}</h2>
              <span className="text-lg font-bold text-[#34d399]">{item.opportunity}</span>
            </div>
            <p className="text-[11px] text-[#8b95a7]">
              Competition {item.competition} · median views {formatCount(item.median_views)}
              {item.covered_by_channel ? ' · already on this channel' : ' · not posted recently'}
            </p>
            <ul className="text-[11px] text-[#c5cedb] space-y-1">
              {(item.example_titles || []).slice(0, 3).map((title) => (
                <li key={title} className="truncate">{title}</li>
              ))}
            </ul>
            <button
              type="button"
              onClick={() => navigate(`/dashboard?create=${encodeURIComponent(item.query)}`)}
              className="text-[11px] font-bold text-[#34d399]"
            >
              Use in next video
            </button>
          </article>
        ))}
      </div>
    </main>
  );
}

export default ChannelKeywordsPage;
